import { useEffect, useCallback, useRef } from "react";
import decode from "jwt-decode";
import * as api from "../api";

// Renew this far before the token actually expires, so a slow request or a
// briefly offline till still has room to complete the swap.
const RENEW_LEAD_MS = 5 * 60 * 1000; // 5 minutes
// Never schedule a timer tighter than this (avoids a hot loop if the clock skews).
const MIN_DELAY_MS = 15 * 1000;

const readProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("profile") || "null");
  } catch (err) {
    return null;
  }
};

const getExpiryMs = (token) => {
  try {
    const { exp } = decode(token);
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (err) {
    return null;
  }
};

/**
 * Keeps an active session alive.
 *
 * The access token is short-lived, and Header.js signs the user out as soon as
 * it expires — which is why a till in use gets kicked to the login screen. This
 * swaps the token for a fresh one shortly before it lapses, and again whenever
 * the tab regains focus (covering machines that slept through the timer).
 *
 * Renewal only works while the current token is still valid, so an abandoned
 * session still expires on its own rather than living forever.
 */
export default function useSessionKeepAlive() {
  const timerRef = useRef(null);
  // Guards against a focus event and a timer firing a request at the same time.
  const inFlightRef = useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleRef = useRef(null);

  const renew = useCallback(async () => {
    const profile = readProfile();
    if (!profile?.token || inFlightRef.current) return;

    const expiresAt = getExpiryMs(profile.token);
    // Already expired — nothing to renew; the existing logout path handles it.
    if (expiresAt && expiresAt <= Date.now()) return;

    inFlightRef.current = true;
    try {
      const { data } = await api.refreshSession();
      if (data?.token) {
        const latest = readProfile() || {};
        localStorage.setItem(
          "profile",
          JSON.stringify({ ...latest, token: data.token })
        );
      }
    } catch (err) {
      // A 401 means the current credential cannot renew. Keeping it would
      // make this hook retry the same rejected token every 15 seconds.
      if (err?.response?.status === 401) {
        localStorage.removeItem("profile");
      }
    } finally {
      inFlightRef.current = false;
      if (scheduleRef.current) scheduleRef.current();
    }
  }, []);

  const schedule = useCallback(() => {
    clearTimer();
    const profile = readProfile();
    if (!profile?.token) return;

    const expiresAt = getExpiryMs(profile.token);
    if (!expiresAt) return;

    const delay = Math.max(expiresAt - Date.now() - RENEW_LEAD_MS, MIN_DELAY_MS);
    timerRef.current = setTimeout(renew, delay);
  }, [renew]);

  // `renew` re-schedules through this ref to avoid a circular dependency.
  scheduleRef.current = schedule;

  useEffect(() => {
    if (!readProfile()?.token) return undefined;

    schedule();

    // A sleeping machine never fires the timer, so re-check on wake/refocus.
    const handleVisible = () => {
      if (document.visibilityState !== "visible") return;
      const profile = readProfile();
      if (!profile?.token) return;

      const expiresAt = getExpiryMs(profile.token);
      if (expiresAt && expiresAt - Date.now() < RENEW_LEAD_MS) {
        renew();
      } else {
        schedule();
      }
    };

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleVisible);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleVisible);
    };
  }, [schedule, renew]);
}
